import { useState, useEffect } from "react";
import { supabase } from "@/gestionale/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/gestionale/components/ui/button";
import { Input } from "@/gestionale/components/ui/input";
import { Label } from "@/gestionale/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/gestionale/components/ui/card";
import { Checkbox } from "@/gestionale/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState<"invite" | "recovery">("recovery");
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  const navigate = useNavigate();

 useEffect(() => {
    const hash = window.location.hash;
    console.log("[ResetPassword] Componente montato. Hash URL:", hash);

    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const urlType = params.get("type");
    console.log("[ResetPassword] Parsed - accessToken:", accessToken ? "presente" : "ASSENTE", "refreshToken:", refreshToken ? "presente" : "ASSENTE", "type:", urlType);

    if (urlType === "invite" || urlType === "recovery") {
      setMode(urlType === "invite" ? "invite" : "recovery");
    }

    if (accessToken && refreshToken) {
      // Stabilisce esplicitamente la sessione dai token presenti nell'URL
      // (necessario perché in questa versione di supabase-js il flusso
      // invite/recovery non genera sempre un evento onAuthStateChange
      // con sessione valida in automatico).
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }).then(({ error }) => {
        if (error) {
          console.error("[ResetPassword] Errore setSession:", error);
        } else {
          console.log("[ResetPassword] Sessione stabilita con successo");
        }
        setReady(true);
      });
    } else {
      // Fallback: ascolta comunque eventuali eventi auth (es. PASSWORD_RECOVERY
      // generato da supabase.auth.resetPasswordForEmail in altri flussi)
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        console.log("[ResetPassword] Evento auth:", event, "Sessione presente:", !!session);
        if (event === "PASSWORD_RECOVERY") {
          setMode("recovery");
          setReady(true);
        }
      });
      setReady(true);
      return () => subscription.unsubscribe();
    }

  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { data, error } = await supabase.auth.updateUser({ password });

    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }

    if (mode === "invite") {
      // Attiva davvero l'utente solo ora: assegna ruolo, collega la persona
      // e imposta il profilo come attivo. Prima di questo momento l'utente
      // esiste in auth ma NON è ancora operativo nel gestionale.
      //
      // NOTA IMPORTANTE: l'iscrizione alla newsletter (sotto) è
      // volutamente INDIPENDENTE da questo controllo. In precedenza, se
      // confirm-invite falliva, il codice usciva subito con un `return`
      // e l'iscrizione alla newsletter non veniva mai tentata, anche se
      // l'utente aveva spuntato la checkbox — perdendo così iscritti ogni
      // volta che l'attivazione account aveva un problema. Le due cose
      // non devono dipendere l'una dall'altra.
      const { error: confirmError } = await supabase.functions.invoke("confirm-invite");
      if (confirmError) {
        console.error("[ResetPassword] Errore conferma invito:", confirmError);
        toast.error("Password creata, ma l'attivazione account ha avuto un problema. Contatta l'amministratore.");
      }

      if (subscribeNewsletter) {
        const email = data.user?.email;
        if (email) {
          try {
            await supabase.from("newsletter").insert({ email });
          } catch {
            // Ignora errori (es. email già iscritta) - non deve bloccare il login
          }
        }
      }

      if (confirmError) {
        setLoading(false);
        return;
      }
    }

    toast.success(mode === "invite" ? "Benvenuto!" : "Password aggiornata!");
    navigate("/");
    setLoading(false);
  };

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{mode === "invite" ? "Crea la tua password" : "Nuova Password"}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {mode === "invite"
              ? "Benvenuto! Scegli una password per accedere al gestionale."
              : "Inserisci la nuova password per il tuo account."}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">
                {mode === "invite" ? "Scegli una password" : "Nuova password"}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
               placeholder="Minimo 6 caratteri"
                />
              </div>

              {mode === "invite" && (
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="newsletter"
                    checked={subscribeNewsletter}
                    onCheckedChange={(checked) => setSubscribeNewsletter(checked === true)}
                  />
                  <Label htmlFor="newsletter" className="text-sm font-normal cursor-pointer leading-snug">
                    Iscrivimi alla newsletter dell'ASD Rubiana Calcio per ricevere novità e aggiornamenti
                  </Label>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "invite" ? "Accedi al gestionale" : "Aggiorna password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}